export interface ContentArchiveEntry {
  slug: string;
  title: string;
  sourcePath: string;
  dateArchived: string;
  reason: string;
  content: string;
}

export const CONTENT_ARCHIVE: ContentArchiveEntry[] = [
  {
    slug: "solar-pv-inspect-first-strip",
    title: "We Design For Your Roof (Inspect-First Strip)",
    sourcePath: "/solar-pv",
    dateArchived: "2026-07-09",
    reason: "Removed inspect-first strip section from live /solar-pv page",
    content: `## We Design For Your Roof

**We Design For Your Roof.** We assess **orientation, pitch, shading and roof condition**, plus your **electrics and DNO requirements**, before proposing a system.`,
  },
  {
    slug: "solar-pv-roof-assessment-items",
    title: "Roof Assessment Callout Items (04 & 06)",
    sourcePath: "/solar-pv",
    dateArchived: "2026-07-09",
    reason: "Two callouts removed from the Roof Assessment section item list (was 6 items, now 4)",
    content: `## Roof Assessment — Removed Callout Items

Two callouts pulled out of the Roof Assessment section's item list.

### 04 — Roof Safety Check

- Support line: Confirms the roof is suitable
- Tooltip: The roof must be suitable before panels are recommended.

### 06 — Grid Paperwork Included

- Support line: We handle the required paperwork
- Tooltip: Grid paperwork is handled as part of the process where required.`,
  },
  {
    slug: "solar-pv-installation-day",
    title: "What Happens On The Day",
    sourcePath: "/solar-pv",
    dateArchived: "2026-07-09",
    reason: "Removed installation day walkthrough section from live /solar-pv page",
    content: `## Installation Day(s)

### What Happens On The Day

#### Step 01 — Scaffold & Access

- Erect scaffold and set up safe roof access
- Protect access routes and the work area
- Confirm the work plan with you

#### Step 02 — Mount The Array

- Fix mounting rails to the roof
- Lay out and mount the panel array
- Keep the roof watertight throughout

#### Step 03 — Inverter, Battery & Electrics

- Install the inverter and, where included, the battery
- Complete the electrical work and DNO/G99 compliance steps

#### Step 04 — Commission & Handover

- Commission the system with full records
- Demonstrate the monitoring app and hand over
- Register the MCS certificate and manufacturer warranties

### Good To Know

- Most residential installs take 1-2 days on the roof, confirmed in your proposal.
- We protect access routes and tidy up scaffold strike on completion.
- We won't leave until commissioning and monitoring are set up.`,
  },
  {
    slug: "solar-pv-installation-facts-strip",
    title: "Installation Facts Strip",
    sourcePath: "/solar-pv",
    dateArchived: "2026-07-09",
    reason: "Removed installation facts strip from live /solar-pv page",
    content: `## Installation Facts Strip

| Label | Value |
| --- | --- |
| Timeline | Typically 1-2 Days |
| Team | MCS-Certified Installers |
| System | Panels + Inverter (+ Optional Battery) |
| Warranties | Manufacturer-Backed |
| Payment | On Completion |`,
  },
  {
    slug: "solar-pv-commercial-cross-sell",
    title: "Commercial Solar PV Cross-Sell",
    sourcePath: "/solar-pv",
    dateArchived: "2026-07-09",
    reason: "Removed commercial cross-sell band from live /solar-pv page",
    content: `## For Businesses & Landlords

### Commercial Solar PV Is Handled Separately

For commercial roofs, portfolio-scale installs, landlord and managing-agent works, see our dedicated commercial solar page. Different design parameters, different DNO conversation, different reporting.

**CTA:** Commercial Solar PV → /commercial-solar-pv`,
  },
  {
    slug: "solar-pv-handover-pack",
    title: "Your Handover Pack",
    sourcePath: "/solar-pv",
    dateArchived: "2026-07-09",
    reason: "Removed handover pack section from live /solar-pv page",
    content: `## Your Handover Pack

Everything documented on completion, so the system is registered, warrantied and ready for export.

### MCS Certificate

Issued and registered on completion so you can apply for an SEG tariff with your chosen supplier.

### Manufacturer Warranties

Registered for your panels, inverter and any battery.

### Commissioning Records

Full records of the install and electrical compliance steps.

### Monitoring App Setup

Configured and demonstrated at handover so you can see generation and usage.`,
  },
  {
    slug: "solar-pv-proper-survey-quote",
    title: "A Proper Survey Means A Better Quote",
    sourcePath: "/solar-pv",
    dateArchived: "2026-07-09",
    reason: "Removed survey trust callout from below Pricing cards on live /solar-pv page",
    content: `## A Proper Survey Means A Better Quote

We don't generate instant estimates based on a postcode alone. Before issuing your written proposal, we assess your roof, electrical system and installation requirements, helping avoid unexpected costs later and ensuring the system is designed specifically for your property.

- Roof measured accurately
- Electrical system assessed
- DNO application handled
- MCS certification included`,
  },
  {
    slug: "financing-solar-plan-original",
    title: "Financing Page — Solar Plan (original)",
    sourcePath: "/financing",
    dateArchived: "2026-07-09",
    reason: "Page rebuilt as finance-led; solar-plan-specific framing retired",
    content: `## Hero

**Eyebrow:** Hometree Solar Plan

### Go Solar With No Upfront Cost

Solar and battery storage across Wales and the South West of England through the Hometree Solar Plan. Installation, monitoring, unlimited repairs and replacement protection included for the life of your plan.

**Small print:** Subject to status, property suitability and a hire agreement.

**Trust badges:**

- MCS-Certified Installation
- Finance By FCA-Regulated Hometree Finance
- No Upfront Cost Option
- All-Inclusive Cover
- No-Obligation Application

## Ways To Spread The Cost

**Eyebrow:** Financing

Finance and funded options are available across the services we offer, with the Hometree Solar Plan as our flagship no-upfront-cost route for solar and battery.

[GATED - Hometree/legal to approve wording]

## An All-Inclusive Way To Go Solar Without The Upfront Cost

**Eyebrow:** What It Is

The Hometree Solar Plan is an all-inclusive way to go solar without the upfront cost. It's a hire agreement, Hometree owns and looks after the system, and you get full use of it, the energy it generates, the savings and the export income, with monitoring and unlimited repairs included for the life of your plan.

## The Benefits Of The Plan

**Eyebrow:** Why People Choose It

### No Upfront Cost Option

Get a solar and battery system installed with nothing to pay up front.

### Affordable Monthly Payments

Spread the cost over 5 to 25 years to suit your household.

### All-Inclusive Cover

Unlimited repairs, no callout fees and no excess for the life of your plan.

### Replacement Protection

Protection continues even after the manufacturer warranty ends.

## Benefits Pill Strip

- No Upfront Cost
- All-Inclusive Cover
- MCS-Certified Install
- Lifetime Monitoring
- No Obligation

## Everything In One Plan

**Eyebrow:** What's Included

- Solar PV system (panels and inverter)
- Optional battery storage
- MCS-certified installation, including scaffolding and access
- Lifetime remote monitoring
- Periodic on-site inspections
- Unlimited repairs, no callout fees, no excess
- Replacement protection even after the manufacturer warranty ends
- No payment until the system is installed and commissioned

## Five Steps From Enquiry To Switch-On

**Eyebrow:** How It Works

### Step 1 — Tell Us About Your Home

A short eligibility form, postcode, ownership and roof basics.

### Step 2 — Design And Estimate

We design your system and share a personalised estimate.

### Step 3 — Apply In Around 2 Minutes

Application won't affect your credit score and is no obligation.

### Step 4 — MCS-Certified Install

Installed by our MCS-certified engineers.

### Step 5 — Payments Begin Once Commissioned

Payments only start once it's commissioned and you're happy.

## Who Can Apply

**Eyebrow:** Eligibility

Available to homeowners (owner-occupiers and individual private landlords) with residential properties in England and Wales. Not available to tenants, landlords operating through a limited company, properties in Scotland, or commercial premises. Subject to status and a hire agreement.

**Small print:**

Offers are subject to status, property suitability and a hire agreement. Monthly payments increase by 2.5% each year. You can settle your plan at any time. Missing payments could have severe consequences and make obtaining credit more difficult.`,
  },
  {
    slug: "financing-faq-original",
    title: "Financing Page — FAQ (original)",
    sourcePath: "/financing",
    dateArchived: "2026-07-09",
    reason: "FAQ rewritten to be finance-generic and provider-agnostic",
    content: `## Common Questions

**Eyebrow:** FAQs

### Is it a loan or a lease?

It's a hire agreement, not a loan. Hometree owns the system and looks after it. You get full use of the system, the energy it generates, the savings and the export income, in return for a monthly payment.

### Who owns the panels during and after the plan?

Hometree owns the system throughout the plan. End-of-term ownership and handover arrangements are confirmed in writing in your hire agreement before you sign.

### Do my payments stay the same?

No. Monthly payments increase by 2.5% each year for the life of the plan. The annual increase is set out clearly in your agreement.

### What's included, and what do I pay for repairs?

Repairs are included for the life of your plan, no callout fees and no excess. Lifetime remote monitoring, periodic on-site inspections and replacement protection (even after the manufacturer warranty ends) are also included.

### Will applying affect my credit score?

No, applying won't affect your credit score at application stage. Any later checks will be explained to you before they happen.

### What if I move home?

You can settle the plan at any time with no early-settlement surcharge, or, if you move, a buyer can take the plan over as part of the sale, subject to the usual checks.

### What is a restriction on title?

A restriction is registered against your property's title at the Land Registry. It is not a charge, gives no right to repossess your home, and is removed when the plan ends.

### Can I have a battery?

Yes. Battery storage is available as part of the plan and is designed around your home's usage. Battery sizing is confirmed at the design stage.

### Do I keep the export payments (SEG)?

Yes. You keep the energy savings and the export income from the system you have on your roof.

### Am I eligible?

Available to homeowners (owner-occupiers and individual private landlords) in England and Wales, with a suitable residential property. Not available to tenants, landlords operating through a limited company, properties in Scotland, or commercial premises. Subject to status and a hire agreement.`,
  },
  {
    slug: "financing-provider-disclosure-original",
    title: "Financing Page — Regulatory Disclosure (original)",
    sourcePath: "/financing",
    dateArchived: "2026-07-09",
    reason:
      "Provider-specific disclosure replaced by gated provider block pending confirmation of finance partner arrangements",
    content: `## Regulatory Disclosure

Complete Remedial Solutions (CRS) Ltd, trading as CRS Energy, is an Appointed Representative of HTG Finance Limited, which is authorised and regulated by the Financial Conduct Authority (FRN 825534). Finance is provided by Hometree Finance.

**Note:** Exact registered AR entity wording to be confirmed with Hometree before publishing.

Offers are subject to status, property suitability and a hire agreement. Monthly payments increase by 2.5% each year. You can settle your plan at any time. Missing payments could have severe consequences and make obtaining credit more difficult.

HTG Finance Limited, FRN 825534. Authorised and regulated by the Financial Conduct Authority.

## Form Consent Checkbox Label

I'm happy for CRS Energy to pass my details to Hometree Finance to process an application. *`,
  },
  {
    slug: "financing-form-original",
    title: "Financing Page — Eligibility Form (original)",
    sourcePath: "/financing",
    dateArchived: "2026-07-09",
    reason: "Form simplified; pre-qualification fields moved to follow-up call",
    content: `## Eligibility Form — Field Specification

The same form appears twice on the page: once in the hero (\`idPrefix="hero"\`) and once in the bottom CTA section (\`idPrefix="bottom"\`).

### Hero Form Wrapper Copy

**Eyebrow:** No-Obligation Application

**Heading:** Check If My Home Qualifies

**Intro:** A 2-minute eligibility check. Won't affect your credit score.

### Bottom CTA Wrapper Copy

**Heading:** Check If Your Home Qualifies

**Intro:** A short eligibility check, or call 0800 009 2992.

### Fields (both instances)

| Field label | Name | Required | Options / notes |
| --- | --- | --- | --- |
| Name | name | Yes | Text input |
| Mobile | phone | Yes | Tel input |
| Email (optional) | email | No | Email input |
| Postcode | postcode | Yes | Placeholder: "Confirms England / Wales" |
| Are you the homeowner? | ownership | Yes | Owner-occupier; Private landlord (individual); Landlord via a limited company; I rent |
| Property & roof type | property_type | No | Detached house; Semi-detached house; Terraced house; Bungalow; Other |
| Approx. monthly electricity spend | monthly_spend | No | Under £80 / month; £80 - £150 / month; £150 - £250 / month; Over £250 / month; Not sure |
| Interested in a battery? | battery | No | Yes; No; Not sure |
| Best time to call | best_time | No | Morning; Afternoon; Evening; Anytime |
| Consent checkbox | consent | Yes | "I'm happy for CRS Energy to pass my details to Hometree Finance to process an application." |

### Conditional Warning (ownership field)

Shown when ownership is "Landlord via a limited company" or "I rent":

This plan may not fit your situation, but we can still help. Submit the form and our team will be in touch with alternative routes.

### Submit Button

Check If My Home Qualifies

### Post-Submit Microcopy (below button)

Subject to status, property suitability and a hire agreement.

### Hidden / Internal Fields

- Honeypot: company_website (hidden)
- service_of_interest (submitted as): Hometree Solar Plan
- source_page (submitted as): /financing`,
  },
  {
    slug: "financing-misc-copy-original",
    title: "Financing Page — Misc Copy (original)",
    sourcePath: "/financing",
    dateArchived: "2026-07-09",
    reason:
      "Product-specific risk lines and gated sections removed during finance-led page rebuild",
    content: `## Page Meta

**Title:** Hometree Solar Plan | No Upfront Cost Solar Finance | CRS Energy

**Description:** Go solar with no upfront cost through the Hometree Solar Plan. MCS-certified install, monitoring, unlimited repairs and replacement protection included for the life of your plan.

## Product-Specific Risk Lines (removed from live page)

Monthly payments increase by 2.5% each year.

Missing payments could have severe consequences and make obtaining credit more difficult.

## Finance Across Our Services (gated section)

**Eyebrow:** Finance Across Our Services

**Heading:** Where Finance Applies Across Our Work

**Intro:** [GATED for review] Some services have a dedicated finance product, others may be delivered through funded grant routes (for example ECO or ORP) which are not consumer credit. The cards below show current status only. Specific product detail and any regulatory wording will be added once each is signed off.

### Solar & Battery

- Badge: Available
- Description: The Hometree Solar Plan: an all-inclusive way to go solar without the upfront cost.
- CTA: View Solar Plan

### Heat Pumps

- Badge: Funded Routes
- Description: [GATED, confirm product, lender and sign-off]
- CTA: [CTA placeholder]

### Boilers & Heating

- Badge: [CONFIRM]
- Description: [GATED]
- CTA: [CTA placeholder]

### Insulation

- Badge: Funded Routes
- Description: [GATED, note ECO/ORP funded routes are not consumer credit]
- CTA: [CTA placeholder]

**Regulatory copy placeholder (on gated cards):** [GATED, product-specific risk warnings, representative example and regulatory disclosure to be added once the product, lender and sign-off are confirmed. Do not reuse the Hometree hire-agreement wording for this service.]

**Closing note:** [GATED for review] Consumer finance (a regulated credit or hire agreement you enter into) is different from funded routes such as ECO or ORP, which are grant-style schemes delivered through approved installers and are not consumer credit. Eligibility, terms and any costs differ by product and property.

## Solar PV Cross-Link

**Heading:** Looking For The Technical & Install Detail?

**Body:** See our Solar PV service page for design, MCS certification, DNO/G99 and battery options.

**CTA:** View Solar PV → /solar-pv

## Funding Options For More Than Solar (other services grid)

**Eyebrow:** Other Services

**Heading:** Funding Options For More Than Solar

**Intro:** Depending on the work and your property, finance or funded routes may be available for other energy efficiency and home repair services. A member of the team can explain the options that apply to your property and confirm any eligibility requirements.

**Footer callout — Not Sure Which Service Fits?:** Call us to discuss the work and we'll explain the payment and funding options that may apply.`,
  },
  {
    slug: "provider-references-sitewide-original",
    title: "Provider References Removed Site-Wide",
    sourcePath: "various — see content",
    dateArchived: "2026-07-09",
    reason: "Lender-agnostic rule: provider names retired from live site except FinanceProviderDisclosure placeholder",
    content: `## src/components/solar-finance-form.tsx

**Form consent checkbox label:**
I'm happy for CRS Energy to pass my details to Hometree Finance to process an application.

**Webhook/message line:**
Consent to share with Hometree Finance: Yes/No

**service_of_interest (submitted value):**
Hometree Solar Plan

**Fit warning (landlord/tenant):**
This plan may not fit your situation, but we can still help.

## src/components/solar-finance-eligibility.tsx

**Default body copy:**
Finance options are available through our finance partner, so the cost of a designed installation can be spread rather than paid in full on completion.

**Secondary line:**
Full APR, representative example, eligibility and FCA wording are provided in writing alongside your written proposal. We don't quote generic savings figures.

## src/components/home-finance-details.tsx

**Spread The Cost paragraph:**
Spread the cost of insulation, heating upgrades and renewables with finance options through our finance partner, subject to eligibility and written terms.

**Secondary line:**
Full APR, representative example, eligibility and FCA wording are provided in writing alongside your written proposal.

## src/routes/services.tsx

**SolarFinanceEligibility bodyCopy:**
Many of the measures we install can be financed through our finance partner, so you can spread cost rather than pay in full on completion, subject to eligibility.

## src/components/landing/lp-solar-savings.tsx (LpSolarSavingsBridge)

**Finance promo paragraph:**
Prefer not to pay upfront? Our Solar Plan swaps the upfront cost for one monthly payment — so the savings above start from day one, with nothing to pay until your system is up and running.

**CTA link target:** #solar-finance

## src/components/landing/lp-solar-finance-accordion.tsx

**How It Works panel:**
It's a hire agreement, the plan owns and maintains the system, and you get full use of it.

## public/llms.txt

**Financing page summary:**
Hometree Solar Plan and solar finance options.`,
  },
  {
    slug: "ashp-hero-form-original",
    title: "ASHP Page — Hero Form (original)",
    sourcePath: "/air-source-heat-pumps",
    dateArchived: "2026-07-09",
    reason:
      "Pre-edit snapshot of hero enquiry form wrapper copy and field list; replaced by stepped AshpHeroForm",
    content: `## Hero Form Wrapper Copy

**Eyebrow:** Get Started

**Heading:** Book Your Free Assessment

**Intro:** Submit your details to arrange your **free, no-obligation heat pump assessment**.

## Form Configuration

| Property | Value |
| --- | --- |
| Component | \`EnquiryForm\` |
| formType | quote |
| variant | lp-quote |
| defaultService | Air Source Heat Pump |
| sourcePage | /air-source-heat-pumps |
| submitLabel | Submit |

## Fields (lp-quote variant)

| Field label | Name | Required | Notes |
| --- | --- | --- | --- |
| Name | name | Yes | Text input |
| Phone | phone | Yes | Tel input |
| Email (optional) | email | No | Email input |
| Postcode | postcode | Yes | Text input |
| Anything we should know? (optional) | message | No | Textarea, 3 rows, max 5000 characters |

**Not shown on this variant:** Service of interest (hidden; submitted as defaultService).

## Hidden / Internal Fields

- Honeypot: company_website (hidden)
- service_of_interest (submitted as): Air Source Heat Pump
- source_page (submitted as): /air-source-heat-pumps

## Submit Button

Submit

## Post-Submit Microcopy (below button)

By submitting you agree to our privacy policy. We never share your details.`,
  },
  {
    slug: "ashp-heat-day-chart-original",
    title: "ASHP Page — How Homes Heat Through The Day (original)",
    sourcePath: "/air-source-heat-pumps",
    dateArchived: "2026-07-09",
    reason:
      "Replaced by ComfortCurve component; pre-edit snapshot of single-line BoilerVsHeatPumpChart",
    content: `## Illustrative Block

**Label:** Illustrative

**Heading:** How Homes Heat Through The Day

## Toggle (tablist aria-label: Heating comparison)

| Tab | Default active |
| --- | --- |
| Boiler | Yes (initial mode) |
| Heat Pump | No |

## SVG Chart Config

- viewBox: \`0 0 400 110\`
- role: img
- Y-axis labels: Warmer (top), Cooler (bottom)
- X-axis ticks: 6am, 12pm, 4pm, 8pm, 12am
- Boiler path: \`M 20 90 L 55 90 L 65 25 L 85 25 L 95 90 L 175 90 L 185 30 L 205 30 L 215 90 L 380 90\`
- Heat pump path: \`M 20 62 C 60 58 100 55 140 55 C 180 55 220 60 260 68 C 300 74 340 70 380 62\`
- Boiler stroke: accent token
- Heat pump stroke: \`hsl(210 65% 50%)\`
- Transition: 700ms ease on path and stroke
- House icon at line end (x≈370, y≈55)

## aria-label (per mode)

- Boiler: "Line shows sharp morning and evening heat spikes with troughs between"
- Heat pump: "Line shows steady gentle warmth across the day"

## Caption (per mode, italic)

- Boiler: "Boilers tend to blast on then off, so rooms warm quickly then cool between cycles."
- Heat pump: "Heat pumps run gently and steadily so the home stays at a comfortable temperature rather than swinging."

## Footnote

Illustrative - showing how the two heat differently, not specific temperatures.

## Screen-reader-only description (per mode)

- Boiler: "The boiler line spikes sharply in the morning and evening with troughs between, showing on-off heating."
- Heat pump: "The heat pump line is a gentle flat curve across the day, showing steady comfort."

## Section placement

Rendered inside the "A Heat Pump Isn't A Boiler Swap" section (right column on large screens, below the three WHY_CARDS on small screens).`,
  },
  {
    slug: "ashp-duplicate-blocks-original",
    title: "ASHP Page — Duplicate / Restated Blocks (original)",
    sourcePath: "/air-source-heat-pumps",
    dateArchived: "2026-07-09",
    reason:
      "Pre-edit snapshot of restated boiler-swap, trust and insulation-first copy; updated after live dedup pass (Jul 2026)",
    content: `## Section A — "A Heat Pump Isn't A Boiler Swap" (inline band)

**Eyebrow:** Why It Matters

**Heading:** A Heat Pump Isn't A Boiler Swap

**Intro:** The technology is well-proven. What makes the difference between a comfortable, efficient system and a disappointing one is the design and commissioning behind it.

### Card 1 — How A Heat Pump Works

A heat pump moves heat from outside air into your home, delivering it through radiators, underfloor heating and a hot-water cylinder, at a lower running temperature (what installers call flow temperature) than a typical boiler.

### Card 2 — Design & Sizing Matter

A heat pump only performs as well as the system around it. The heat-loss calculation, radiator sizing and cylinder choice are what make or break comfort and efficiency.

### Card 3 — Insulation First, Heating Second

The lower the heat loss, the better any heat pump performs. We look at insulation and ventilation alongside the heating, not in isolation.

---

## Section B — Trust band (LpOrderedSections, trust section)

**Heading:** Designed, Coordinated, Commissioned

**Body:** A heat pump isn't a boiler swap. We treat it as a coordinated, fabric-first retrofit measure: design first, install second, commission properly, register the warranty and the MCS certificate on completion.

### Benefits grid (trust section)

| Title | Description |
| --- | --- |
| Designed System | Heat-loss-led design instead of a guessed swap-out. |
| Quieter, Steady Heat | Lower, steadier flow temperatures and balanced emitters. |
| Coordinated Retrofit | Fabric, ventilation and controls considered alongside the heat source. |
| Documented Install | MCS certificate and manufacturer warranty registered on completion. |

---

## Section C — "Why It Matters" carousel slide 2 (insulation-first restatement)

**Slide title:** Insulation First, Heating Second

**Intro:** The lower the heat your home loses, the better any heat pump will perform. We consider insulation and ventilation alongside the heat source, not as an afterthought.

**Bullets:**

- Insulation and airflow reviewed at survey.
- Coordinated retrofit if wider fabric work is needed.
- Same team, one accountable design.

---

## Section D — Coordinated Retrofit band (primary, insulation-first)

**Eyebrow:** Coordinated Retrofit

**Heading:** A Heat Pump Performs Best Alongside Good Insulation & Ventilation

**Body:** The lower the heat loss, the lower the running temperature (what installers call flow temperature) the heat pump can run at, the better its real-world efficiency. CRS handles insulation and ventilation in-house, so the wider retrofit joins up rather than being treated as a separate problem.

**CTAs:**

- Loft Insulation → /loft-insulation
- Internal Wall Insulation → /internal-wall-insulation
- Ventilation & Extraction → /services/ventilation-extraction

---

## Content file mirror (air-source-heat-pumps.ts problemPoints — same themes, not rendered as this inline section)

### How A Heat Pump Works

An air source heat pump moves heat from the outside air into your home, delivering it through radiators, underfloor heating and a hot-water cylinder, at a lower running temperature (what installers call flow temperature) than a typical boiler.

### Design And Sizing Matter

A heat pump only performs as well as the system around it. Heat-loss calculation, radiator sizing and cylinder choice are what make or break real-world comfort and efficiency.

### Insulation First, Heating Second

The lower the heat loss, the better any heat pump performs. We look at insulation and ventilation alongside the heating, not in isolation.

---

## Dedup pass (Jul 2026) — removed from live page

### WHY_CARDS — Insulation First, Heating Second (card removed)

**Title:** Insulation First, Heating Second

**Body:** The lower the heat loss, the better any heat pump performs. We look at insulation and ventilation alongside the heating, not in isolation.

### Carousel slide — Insulation First, Heating Second (replaced with Quieter, Steady Heat)

**Title:** Insulation First, Heating Second

**Intro:** The lower the heat your home loses, the better any heat pump will perform. We consider insulation and ventilation alongside the heat source, not as an afterthought.

**Bullets:**

- Insulation and airflow reviewed at survey.
- Coordinated retrofit if wider fabric work is needed.
- Same team, one accountable design.

### Boiler swap section — original intro (replaced)

The technology is well-proven. What makes the difference between a comfortable, efficient system and a disappointing one is the design and commissioning behind it.

### MCS carousel slide — boiler-swap phrase removed from intro

You get a real handover pack, not just a boiler swap. Certificates, commissioning records and manufacturer warranties, all registered on completion.

### Assess card — original insulation why (replaced)

Insulation and airflow are assessed alongside the heating, so the system isn't fighting the building.`,
  },
  {
    slug: "ashp-council-block-original",
    title: "ASHP Page — Working With Councils & Housing Associations (original)",
    sourcePath: "/air-source-heat-pumps",
    dateArchived: "2026-07-09",
    reason:
      "Pre-edit snapshot of inline B2B council / housing association band; replaced by slim AshpCouncilsBanner",
    content: `## Section — Merged B2B strip (after reviews/pricing, before FAQs)

**Eyebrow:** Social Housing, Councils & Portfolio Buyers

**Heading:** Working With Councils & Housing Associations

**Body:** Decarbonisation of social-housing and off-gas rural stock, portfolio heat-loss surveys, framework-ready delivery, social value through local jobs and apprenticeships, and KPI / completion reporting suited to landlords and managing agents. Retrofit programmes are handled separately from single-home installs, with different design, reporting and mobilisation.

**CommercialSectionLinks:** compliance, socialValue

**Primary CTA:** Heat Pump Retrofit Programmes → /commercial-heat-pumps

**Secondary CTA:** Talk To Our Team → #bottom-cta`,
  },
  {
    slug: "ashp-funded-finance-copy-original",
    title: "ASHP Page — Funded Routes & Finance Copy (original)",
    sourcePath: "/air-source-heat-pumps",
    dateArchived: "2026-07-09",
    reason:
      "Pre-edit snapshot of funded routes and finance copy before provider-agnostic rewrite and /financing CTA routing",
    content: `## Inline strip — Funded Routes (above Coordinated Retrofit band)

**Lead-in (bold):** Funded Routes.

**Body:** We can quote and deliver eligible funded routes such as the Green Homes plan. Where your home qualifies, we'll explain what that means for your proposal without making funding the focus.

---

## Finance zone (#finance)

**Eyebrow:** Funding & Finance

**Heading:** Grants, Funded Routes & Finance

**Intro:** We'll tell you which routes your home may qualify for and what that means for your written proposal. Nothing here is a quote, everything is confirmed at your free survey.

### Primary Route — Funded Routes

**Eyebrow:** Primary Route

**Heading:** Funded Routes

**Body:** Where your home qualifies, government-backed retrofit programmes can carry some or all of the heat pump upgrade. We're set up to quote and deliver eligible funded routes, and we'll explain what that means for your proposal without making funding the focus.

**Bullets:**

- We check eligibility against current schemes at your free survey.
- Applications, evidence and reporting handled in-house.
- Same MCS design, whether the route is funded or self-funded.

### Alternative Route — Finance

**Eyebrow:** Alternative Route

**Heading:** Finance

**Body:** For homes that aren't funded, we can point you toward regulated finance options once your proposal is written. Everything is tied to your specific property and survey, so we don't quote generic figures here.

**Italic note:** We don't publish representative examples or lender-specific numbers. Any figures come from a real proposal for your home, in writing, before you decide anything.

### Footer callout (primary background)

**Text:** Not sure which route fits your home? Book a call and we'll walk you through it.

**CTAs:**

- Book A Call → #bottom-cta
- Or Call 0800 009 2992

---

## Developer note on live page (not user-facing)

Finance zone copy is placeholder pending product/lender confirmation - do not expand without sign-off.`,
  },
  {
    slug: "ashp-reorder-dropped-sections",
    title: "ASHP Page — Sections Dropped In Conversion Reorder",
    sourcePath: "/air-source-heat-pumps",
    dateArchived: "2026-07-09",
    reason:
      "Removed from live page body during conversion-focused section reorder (funded strip, coordinated retrofit band, installation facts strip, trust band rendered separately)",
    content: `## Funded Routes inline strip

**Lead-in (bold):** Funded Routes.

**Body:** We can quote and deliver eligible funded routes such as the Green Homes plan. Where your home qualifies, we'll explain what that means for your proposal without making funding the focus.

---

## Coordinated Retrofit band

**Eyebrow:** Coordinated Retrofit

**Heading:** A Heat Pump Performs Best Alongside Good Insulation & Ventilation

**Body:** The lower the heat loss, the lower the running temperature (what installers call flow temperature) the heat pump can run at, the better its real-world efficiency. CRS handles insulation and ventilation in-house, so the wider retrofit joins up rather than being treated as a separate problem.

**CTAs:** Loft Insulation → /loft-insulation; Internal Wall Insulation → /internal-wall-insulation; Ventilation & Extraction → /services/ventilation-extraction

---

## Installation facts strip

| Label | Value |
| --- | --- |
| Timeline | Property-Specific |
| Team | MCS-Certified Installers |
| System | Designed (Heat Pump + Cylinder) |
| Warranty | Manufacturer-Backed |
| Payment | On Completion |

---

## Trust band (LpOrderedSections — no longer rendered on page)

**Heading:** Designed, Coordinated, Commissioned

**Body:** A heat pump isn't a boiler swap. We treat it as a coordinated, fabric-first retrofit measure: design first, install second, commission properly, register the warranty and the MCS certificate on completion.`,
  },
  {
    slug: "bus-savings-estimator-cta",
    title: "Want To Estimate Your Annual Savings First?",
    sourcePath: "/boiler-upgrade-scheme",
    dateArchived: "2026-07-09",
    reason: "Removed savings estimator CTA from live BUS page",
    content: `## Want To Estimate Your Annual Savings First?

Use our Home Renewables Savings Estimator to check your potential grant route, annual savings, solar opportunity, battery benefit and carbon reduction before booking a survey.

**CTA:** Book My Free Survey`,
  },
];

export function getContentArchiveEntry(slug: string): ContentArchiveEntry | undefined {
  return CONTENT_ARCHIVE.find((entry) => entry.slug === slug);
}

/** Newest archived entries first; ties broken alphabetically by title. */
export function getContentArchiveEntries(): ContentArchiveEntry[] {
  return [...CONTENT_ARCHIVE].sort(
    (a, b) => b.dateArchived.localeCompare(a.dateArchived) || a.title.localeCompare(b.title),
  );
}
